import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '../../../ui/core';
import {
  DEFAULT_INCLUDED_PRIMARY_TYPES,
  DEFAULT_SEARCH_PARAMS,
  FULL_CAP_REQUESTS,
  FULL_CAP_REQUESTS_WITHOUT_LODGING,
  LODGING_PRIMARY_TYPES,
  MAX_CELLS,
  MAX_REQUESTS,
  PARAM_BOUNDS,
  validateParams,
} from '../../../lib/stationCandidates/searchParams';

/**
 * The sweep's parameter controls plus the pre-spend estimate readout.
 *
 * THIS PANEL'S ENTIRE JOB IS SHOWING MONEY BEFORE IT IS SPENT, so three things
 * are load-bearing rather than cosmetic.
 *
 * 1. EVERY BOUND COMES FROM `PARAM_BOUNDS`, never from a literal typed here.
 *    `validateParams` and these inputs are two views of the same DTO
 *    validators; hardcoding a max in an `inputProps` would let a control accept
 *    a value the validator rejects (or refuse one it allows), and the operator
 *    would meet the disagreement as a 400 from the server.
 *
 * 2. THE TWO COST FIGURES ARE LABELLED AS DIFFERENT THINGS.
 *    `estimatedCostUsd` prices EVERY request in the fan-out. `cachedRequestCount`
 *    is a REAL cache probe (the service counts warm keys), and
 *    `estimatedCostUsdAfterCache` prices only the requests that would actually
 *    reach Google. Reading the pre-cache figure as the bill makes a perfectly
 *    reasonable sweep look unaffordable and gets it abandoned; reading the
 *    post-cache figure as a guarantee under-states the worst case. Both are
 *    shown, each named for what it is.
 *
 * 3. THE CACHE PROBE IS SKIPPED WHEN OVER CAP. The service returns
 *    `cachedRequestCount: 0` there — deliberately, since probing thousands of
 *    keys to price a request it will refuse is waste — so a literal "0 cached"
 *    would be presenting an unmeasured value as a measurement, and the
 *    "after cache" figure equals the pre-cache one for the same reason. Over
 *    cap, both are suppressed and said to be unmeasured.
 *
 * `MAX_CELLS`/`MAX_REQUESTS` are imported only as a pre-estimate hint for the
 * fine print. Once an estimate exists, the ECHOED `maxCells`/`maxRequests` win:
 * they are what that server will actually enforce, and a frontend constant can
 * go stale against a backend bump.
 *
 * No MUI `Slider` here on purpose. It attaches DOM listeners through a ref in a
 * passive effect, which `react-test-renderer`'s host instances cannot satisfy,
 * so every suite that mounts one has to stub it out (see `LaneMap.test.js`) and
 * then asserts on a stub instead of on the shipped control. Number fields with
 * `PARAM_BOUNDS`-derived min/max are testable as shipped.
 */

/** Wire key → operator-facing label. One entry per editable param. */
const NUMBER_FIELDS = [
  {
    key: 'cellRadiusMeters',
    label: 'Cell radius (m)',
    help: 'Bigger cells mean fewer cells, so this is the knob that gets an over-large area under the cell cap.',
  },
  {
    key: 'maxResultsPerCell',
    label: 'Max results per cell',
    help: 'Google caps this at 20. It does not change the request count, only how many places each request returns.',
  },
  {
    key: 'dedupeRadiusMeters',
    label: 'Dedupe radius (m)',
    help: 'Two candidates closer than this collapse to one.',
  },
  {
    key: 'corridorRadiusMeters',
    label: 'Corridor radius (m)',
    help: 'How far off a lane polyline a candidate may sit. Ignored when the corridor filter is off.',
  },
];

/**
 * The four params with NO server default. Blank means OMITTED, and that is not
 * the same as 0: `minRating: 0` is a real filter, and because an unrated place
 * has a null rating rather than a 0 one, sending 0 drops exactly the
 * newly-opened places most worth finding.
 *
 * TWO PAIRS, NOT ONE APPLIED TWICE. A 4.2 hotel and a 4.2 cafe are not the same
 * bar, and hotels carry an order of magnitude more reviews — a single threshold
 * across both classes either waves through mediocre hotels or excludes every
 * cafe worth siting. The labels say which class each governs, because "Min
 * rating" on its own now silently means "of food places".
 */
const UNDEFAULTED_FIELDS = [
  { key: 'minRating', label: 'Min rating (food)' },
  { key: 'minUserRatingCount', label: 'Min review count (food)' },
  { key: 'lodgingMinRating', label: 'Min rating (hotels)' },
  { key: 'lodgingMinUserRatingCount', label: 'Min review count (hotels)' },
];

const TIME_FIELDS = [
  { key: 'openByLocalTime', label: 'Open by (local, food)' },
  { key: 'openUntilLocalTime', label: 'Open until (local, food)' },
];

/**
 * `help` exists on these rows for one reason: three of them stopped applying to
 * every candidate and their labels alone no longer say so.
 */
const TOGGLE_FIELDS = [
  {
    key: 'includeLodging',
    label: 'Include hotels',
    // `count` is declared further down this module, so the numbers are formatted
    // inline rather than reordering the file around one caption.
    help: `Adds ${LODGING_PRIMARY_TYPES.join(' and ')} to the sweep. The cost lever: requests are cells × place types, so a full-cap sweep is ${FULL_CAP_REQUESTS.toLocaleString()} requests with hotels and ${FULL_CAP_REQUESTS_WITHOUT_LODGING.toLocaleString()} without.`,
  },
  {
    key: 'requireDineIn',
    label: 'Require dine-in (food only)',
    help: 'Never applied to hotels: Google rarely populates dineIn for them and unknown counts as failing, so requiring it would drop nearly every hotel.',
  },
  {
    key: 'openAllSevenDays',
    label: 'Open all seven days (food only)',
    help: 'Hotels are held to a 00:00–23:59 window instead, so in practice only effectively-always-open lodging passes on stated hours.',
  },
  { key: 'allowMissingOpeningHours', label: 'Allow unknown opening hours' },
  { key: 'corridorFilterEnabled', label: 'Filter to lane corridors' },
];

/** `inputProps` straight off the shared bounds — never a literal. */
const numberInputProps = (key) => {
  const { min, max, integer } = PARAM_BOUNDS[key] || {};
  return {
    ...(min === undefined ? {} : { min }),
    ...(max === undefined ? {} : { max }),
    step: integer ? 1 : 0.1,
  };
};

const boundsHint = (key) => {
  const { min, max } = PARAM_BOUNDS[key] || {};
  if (min !== undefined && max !== undefined) return `${min}–${max}`;
  if (min !== undefined) return `${min} or more`;
  if (max !== undefined) return `up to ${max}`;
  return '';
};

const usd = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? `$${value.toFixed(2)}` : '—';

const count = (value) => (typeof value === 'number' ? value.toLocaleString() : '—');

/**
 * The blocking message, naming the knob that tripped WITH THE ACTUAL NUMBERS.
 *
 * "Too large" tells an operator nothing actionable; "812 cells, over the limit
 * of 64" tells them roughly how much smaller the box has to get. `maxCells` and
 * `maxRequests` come off the response rather than off this repo's constants, so
 * the number quoted is the one the server will enforce.
 *
 * Exported for direct testing: the wording is the whole point of the state.
 */
export const overCapMessage = (estimate) => {
  if (!estimate?.overCap) return null;

  const {
    cells,
    maxCells = MAX_CELLS,
    requestCount,
    maxRequests = MAX_REQUESTS,
    cellRadiusMeters,
    includedTypeCount,
    overCapReason,
  } = estimate;

  if (overCapReason === 'cells') {
    return (
      `This area needs ${count(cells)} cells, over the limit of ${count(maxCells)} — ` +
      `select a smaller area or raise the cell radius (currently ${count(cellRadiusMeters)} m).`
    );
  }

  if (overCapReason === 'requests') {
    return (
      `This sweep needs ${count(requestCount)} Places requests, over the limit of ` +
      `${count(maxRequests)} — reduce the place types (currently ${count(includedTypeCount)}) ` +
      'or select a smaller area.'
    );
  }

  // Clipped to nothing. The backend clips the search grid to the selected lanes'
  // corridor before pricing, and `estimate` REPORTS a clipped-to-zero sweep
  // (`generate` throws) — so this arrives as an over-cap rather than an error,
  // and the remedy is a lane or a radius, never a smaller area. Saying "select a
  // smaller area" here would send an operator in precisely the wrong direction.
  if (overCapReason === 'lanes') {
    return (
      'No search cells survived the corridor clip: none of the selected lanes pass close ' +
      'enough to this area. Select a lane that runs through it, widen the corridor radius, ' +
      'or turn the corridor filter off to sweep the whole rectangle.'
    );
  }

  // `overCap` true with no reason should be impossible — the service sets them
  // together — but a silently un-explained disabled button is the worst
  // possible fallback, so say what is known.
  return (
    `This sweep is over the service limits (${count(cells)} cells against ${count(maxCells)}, ` +
    `${count(requestCount)} requests against ${count(maxRequests)}). Select a smaller area.`
  );
};

/** `'cafe, bar,'` → `['cafe', 'bar']`. The wire wants a `string[]`. */
export const parseTypeList = (raw) =>
  String(raw ?? '')
    .split(',')
    .map((type) => type.trim())
    .filter(Boolean);

/**
 * The excluded-types field: RAW TEXT WHILE TYPING, PARSED ON COMMIT.
 *
 * It cannot be `value={params.excludedPrimaryTypes.join(', ')}` with a split on
 * every keystroke, which is what it was. That round trip is lossy exactly at the
 * separator: typing `'cafe'` then `','` splits to `['cafe']`, which re-renders as
 * `'cafe'` with the comma gone, so the next character lands on the first type
 * (`'cafeb'`) and a second entry is unreachable by typing at all — only a single
 * paste of `'a, b'` ever worked.
 *
 * So the draft string lives here and only becomes an array on blur or Enter. The
 * effect re-seeds the draft when the committed value changes to something the
 * draft does not already parse to — which is what makes "Reset to defaults" clear
 * the box while a mid-typed `'cafe, '` (parsing to the same list) is left alone.
 *
 * Commit-on-blur is not a compromise here: excluded types are applied AFTER the
 * search, so unlike every other control on this panel they cannot change the
 * request count or the price, and there is nothing to re-quote per keystroke.
 */
const ExcludedTypesField = ({ value = [], error, helperText, onCommit, sx }) => {
  const committed = value.join(', ');
  const [draft, setDraft] = useState(committed);

  useEffect(() => {
    setDraft((previous) => (parseTypeList(previous).join(', ') === committed ? previous : committed));
  }, [committed]);

  const commit = () => onCommit(parseTypeList(draft));

  return (
    <TextField
      name="excludedPrimaryTypes"
      label="Excluded place types"
      size="small"
      fullWidth
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
      error={error}
      helperText={helperText}
      sx={sx}
    />
  );
};

const ReadoutRow = ({ label, value, help }) => (
  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
      {label}
      {help ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {help}
        </Typography>
      ) : null}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

const EstimateReadout = ({ estimate }) => {
  const overCap = !!estimate.overCap;
  const maxCells = estimate.maxCells ?? MAX_CELLS;
  const maxRequests = estimate.maxRequests ?? MAX_REQUESTS;

  /**
   * THE CORRIDOR CLIP IS THE MAIN LEVER ON THIS PRICE, so it is reported on the
   * cells row rather than left implicit. The backend drops grid cells that fall
   * outside the selected lanes' corridor before pricing anything, and requests
   * are cells x place types — so the gap between these two numbers IS the saving,
   * and it is the number that tells an operator whether picking fewer lanes or a
   * tighter radius is worth doing before they spend.
   *
   * Gated on `corridorClipApplied` and not on the counts: the flag is how the
   * response distinguishes "clipped, and nothing happened to fall outside" from
   * "not clipped at all" (corridor filter off, or an older backend that does not
   * send these fields). Inferring it from `before === cells` would report the
   * first case as the second and quietly claim the corridor did nothing.
   */
  const clipApplied = !!estimate.corridorClipApplied;
  const cellsBeforeClip = estimate.cellsBeforeCorridorClip;
  const clippedAway =
    clipApplied && Number.isFinite(cellsBeforeClip) && Number.isFinite(estimate.cells)
      ? cellsBeforeClip - estimate.cells
      : null;

  return (
    <Box>
      <ReadoutRow
        label="Search cells"
        help={
          clipApplied
            ? `Clipped to the selected lanes' corridor from ${count(cellsBeforeClip)}${
                clippedAway ? `, dropping ${count(clippedAway)}` : ''
              }.`
            : undefined
        }
        value={`${count(estimate.cells)} of ${count(maxCells)}`}
      />
      <ReadoutRow
        label="Places requests"
        value={`${count(estimate.requestCount)} of ${count(maxRequests)}`}
      />
      <ReadoutRow label="Place types included" value={count(estimate.includedTypeCount)} />
      <ReadoutRow label="Cell radius used" value={`${count(estimate.cellRadiusMeters)} m`} />

      <Divider sx={{ my: 1 }} />

      <ReadoutRow
        label="Estimated cost, before cache"
        help="Prices every request in the fan-out — the worst case."
        value={usd(estimate.estimatedCostUsd)}
      />

      {overCap ? (
        // Not "0 cached". The service skips the probe entirely over cap, so
        // there is no measurement to report and printing 0 would read as
        // "nothing is cached" — a claim nobody made.
        <ReadoutRow
          label="Already cached"
          help="Not measured: the cache probe is skipped while the sweep is over cap."
          value="—"
        />
      ) : (
        <>
          <ReadoutRow
            label="Already cached"
            help="Measured against the cache, not assumed."
            value={`${count(estimate.cachedRequestCount)} requests`}
          />
          <ReadoutRow
            label="Estimated cost, after cache"
            help="Prices only the requests that would actually reach Google."
            value={usd(estimate.estimatedCostUsdAfterCache)}
          />
        </>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {estimate.sku ?? 'unknown SKU'} · {usd(estimate.unitPriceUsdPerThousand)} per 1,000 requests
      </Typography>
    </Box>
  );
};

/**
 * @param params        the operator's raw (possibly mid-typed) params — NOT
 *                      normalised. `toRequestParams` runs inside the hooks so
 *                      the estimate and the sweep are quoted from identical
 *                      params; this panel must not pre-normalise or the display
 *                      and the request could drift apart.
 * @param estimate      `CandidateEstimateResponseDto`, or null before one lands.
 * @param onGenerate    called only when there is an estimate, it is not over
 *                      cap, no field is invalid, and the lane scope is usable.
 * @param laneScopeMessage
 *                      why the sweep cannot run against the current lane
 *                      selection, or null. Owned by the page, because the
 *                      remedy is a checkbox in ITS lane sidebar rather than a
 *                      field here — `laneIds` is derived from that selection and
 *                      is not a panel param. The backend 400s on an empty
 *                      `laneIds` while the corridor filter is on, so this
 *                      disables Generate for the same reason `overCap` does:
 *                      every reason to withhold a billable click is checked
 *                      before the server has to.
 */
const SearchParamsPanel = ({
  params = DEFAULT_SEARCH_PARAMS,
  onChange,
  onReset,
  estimate = null,
  isEstimating = false,
  estimateError = null,
  onGenerate,
  isGenerating = false,
  hasArea = true,
  laneScopeMessage = null,
}) => {
  const errors = validateParams(params);
  const hasErrors = Object.keys(errors).length > 0;
  const overCap = !!estimate?.overCap;
  const capMessage = overCapMessage(estimate);

  const set = (key) => (value) => onChange?.({ ...params, [key]: value });

  const includedTypes = Array.isArray(params.includedPrimaryTypes)
    ? params.includedPrimaryTypes
    : [];

  // The 13 backend defaults in declared order, plus anything extra the current
  // params carry, so a type set arriving from the URL is never silently
  // un-editable (or worse, dropped on the next toggle).
  const typeOptions = [
    ...DEFAULT_INCLUDED_PRIMARY_TYPES,
    ...includedTypes.filter((type) => !DEFAULT_INCLUDED_PRIMARY_TYPES.includes(type)),
  ];

  // `includeLodging` resolves through the same default the request does, so an
  // unset param reads as the server's `true` rather than as false.
  const lodgingSuppressed =
    !(params.includeLodging ?? DEFAULT_SEARCH_PARAMS.includeLodging);
  const effectiveTypeCount = lodgingSuppressed
    ? includedTypes.filter((type) => !LODGING_PRIMARY_TYPES.includes(type)).length
    : includedTypes.length;

  const toggleType = (type) => () => {
    const next = includedTypes.includes(type)
      ? includedTypes.filter((existing) => existing !== type)
      : [...typeOptions.filter((option) => option === type || includedTypes.includes(option))];
    set('includedPrimaryTypes')(next);
  };

  // `Generate` spends money, so every reason to withhold it is checked here
  // rather than left to the server's 400.
  const generateDisabled =
    !hasArea || !estimate || overCap || hasErrors || isEstimating || isGenerating || !!laneScopeMessage;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Search parameters
        </Typography>
        {onReset ? (
          <Button size="small" onClick={onReset}>
            Reset to defaults
          </Button>
        ) : null}
      </Stack>

      {NUMBER_FIELDS.map(({ key, label, help }) => (
        <TextField
          key={key}
          name={key}
          label={label}
          size="small"
          type="number"
          fullWidth
          value={params[key] ?? ''}
          onChange={(e) => set(key)(e.target.value)}
          error={!!errors[key]}
          helperText={errors[key] || `${help} (${boundsHint(key)})`}
          inputProps={numberInputProps(key)}
          sx={{ mb: 1.5 }}
        />
      ))}

      {UNDEFAULTED_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          name={key}
          label={label}
          size="small"
          type="number"
          fullWidth
          value={params[key] ?? ''}
          onChange={(e) => set(key)(e.target.value)}
          error={!!errors[key]}
          helperText={
            errors[key] ||
            `Leave blank for no threshold (${boundsHint(key)}). Blank is not 0 — 0 would drop unrated places.`
          }
          inputProps={numberInputProps(key)}
          sx={{ mb: 1.5 }}
        />
      ))}

      {TIME_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          name={key}
          label={label}
          size="small"
          fullWidth
          value={params[key] ?? ''}
          onChange={(e) => set(key)(e.target.value)}
          error={!!errors[key]}
          helperText={errors[key] || `24-hour ${PARAM_BOUNDS[key].pattern}`}
          sx={{ mb: 1.5 }}
        />
      ))}

      {TOGGLE_FIELDS.map(({ key, label, help }) => (
        <FormControlLabel
          key={key}
          label={
            help ? (
              <Box>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {help}
                </Typography>
              </Box>
            ) : (
              label
            )
          }
          control={
            // No `size` prop. The theme hard-codes this switch's geometry
            // (root 26x16, thumb 12x12, checked translateX(10px)), so
            // `size="small"` layers MUI's own small-size rules on top — a
            // 16x16 thumb in a 26px root — and the thumb overflows the track
            // as a white blob. None of the other 22 Switch usages in this repo
            // pass a size; the theme's default already is the compact one.
            <Switch
              name={key}
              checked={params[key] ?? DEFAULT_SEARCH_PARAMS[key] ?? false}
              onChange={(e) => set(key)(e.target.checked)}
            />
          }
          // One toggle per row. FormControlLabel is inline-flex, so without a
          // width these flow into each other and wrap mid-label.
          //
          // `alignItems: 'flex-start'` because three of these now carry a help
          // caption: the default centring floats the switch against the middle
          // of a two-line block, which reads as belonging to neither line.
          sx={{ width: '100%', m: 0, mb: 0.5, alignItems: 'flex-start' }}
        />
      ))}

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Included place types ({includedTypes.length})
        </Typography>
        {typeOptions.map((type) => {
          // TICKED HERE AND STILL NOT SEARCHED is the one state this list can
          // get into: `includeLodging: false` removes the two lodging types
          // server-side regardless of this checkbox. Rather than duplicate that
          // rule on the wire — one authority, and it is the backend — the row
          // shows the consequence, so the list never claims a type is being
          // searched when it is not.
          const suppressed = lodgingSuppressed && LODGING_PRIMARY_TYPES.includes(type);
          return (
            <FormControlLabel
              key={type}
              label={
                <Typography
                  variant="body2"
                  color={suppressed ? 'text.disabled' : undefined}
                  sx={suppressed ? { textDecoration: 'line-through' } : undefined}
                >
                  {type}
                </Typography>
              }
              control={
                <Checkbox
                  name={`includedPrimaryTypes:${type}`}
                  size="small"
                  checked={includedTypes.includes(type)}
                  onChange={toggleType(type)}
                />
              }
            />
          );
        })}
        <Typography variant="caption" color="error" display="block">
          {errors.includedPrimaryTypes || ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {lodgingSuppressed
            ? `Requests are cells × types, so each type added multiplies the bill. ${LODGING_PRIMARY_TYPES.join(' and ')} are struck through because “Include hotels” is off — they are dropped from the sweep whether or not they are ticked here, so this sweep searches ${effectiveTypeCount}.`
            : 'Requests are cells × types, so each type added multiplies the bill.'}
        </Typography>
      </Box>

      <ExcludedTypesField
        value={Array.isArray(params.excludedPrimaryTypes) ? params.excludedPrimaryTypes : []}
        onCommit={set('excludedPrimaryTypes')}
        error={!!errors.excludedPrimaryTypes}
        helperText={
          errors.excludedPrimaryTypes ||
          'Comma-separated, applied when you leave the field. Excluded types are filtered out after the search, so they do not reduce the request count.'
        }
        sx={{ mt: 1.5, mb: 1.5 }}
      />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Estimate
      </Typography>

      {!hasArea ? (
        <Typography variant="body2" color="text.secondary">
          Draw a rectangle on the map to price a sweep.
        </Typography>
      ) : null}

      {/* Ahead of the estimate lines and instead of them: with no usable lane
          selection the page holds the estimate back rather than spending a
          request the backend would 400, so there is no price coming and saying
          "Pricing this area…" underneath would be a promise nothing keeps. */}
      {hasArea && laneScopeMessage ? (
        <Alert severity="warning">{laneScopeMessage}</Alert>
      ) : null}

      {estimateError ? <Alert severity="error">{String(estimateError.message ?? estimateError)}</Alert> : null}

      {hasArea && isEstimating && !laneScopeMessage ? (
        <Typography variant="body2" color="text.secondary">
          Pricing this area…
        </Typography>
      ) : null}

      {/* Rendered even while re-estimating: it is labelled stale by the line
          above rather than blanked, because an empty panel mid-drag is harder
          to work with than a number that is visibly being refreshed. */}
      {estimate ? <EstimateReadout estimate={estimate} /> : null}

      {capMessage ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {capMessage}
        </Alert>
      ) : null}

      {hasErrors && !capMessage ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Fix the highlighted fields before generating.
        </Alert>
      ) : null}

      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={generateDisabled}
        onClick={() => onGenerate?.()}
      >
        {isGenerating ? 'Generating…' : 'Generate candidates'}
      </Button>
    </Box>
  );
};

export default SearchParamsPanel;
